import { DossierId } from '../dossier/types';

import {
    SendMessageRequestAction,
    SendMessageSuccessAction,
    SendMessageFailureAction,
    GetMessageRequestAction,
    GetMessageSuccessAction,
    GetMessageFailureAction,
    GetDraftMessageRequestAction,
    GetDraftMessageSuccessAction,
    GetDraftMessageFailureAction,
    SaveDraftMessageAction,
    SaveDraftMessageSuccessAction,
    SaveDraftMessageFailureAction,
    Message,
    ScheduleMessageRequestAction,
    ScheduleMessageFailureAction,
    ScheduleMessageSuccessAction,
    FetchScheduledMessagesRequestAction,
    FetchScheduledMessagesFailureAction,
    FetchScheduledMessagesSuccessAction,
    DeleteScheduledMessageFailureAction,
    DeleteScheduledMessageRequestAction,
    DeleteScheduledMessageSuccessAction,
    MarkMessageAsReadRequestAction,
    MarkMessageAsReadFailureAction,
    MarkMessageAsReadSuccessAction,
    UpdateMessageRequestAction,
    UpdateMessageFailureAction,
    DraftMessageMetadata,
    MessageAttachment,
} from './types';

export const sendMessageRequestAction = 'discussion/SEND_MESSAGE_REQUEST';
export const sendMessageSuccessAction = 'discussion/SEND_MESSAGE_SUCCESS';
export const sendMessageFailureAction = 'discussion/SEND_MESSAGE_FAILURE';
export const updateMessageRequestAction = 'discussion/UPDATE_MESSAGE_REQUEST';
export const updateMessageFailureAction = 'discussion/UPDATE_MESSAGE_FAILURE';
export const getMessageRequestAction = 'discussion/GET_MESSAGE_REQUEST';
export const getMessageSuccessAction = 'discussion/GET_MESSAGE_SUCCESS';
export const getMessageFailureAction = 'discussion/GET_MESSAGE_FAILURE';
export const markMessageAsReadRequestAction = 'discussion/MARK_MESSAGE_AS_READ_REQUEST';
export const markMessageAsReadSuccessAction = 'discussion/MARK_MESSAGE_AS_READ_SUCCESS';
export const markMessageAsReadFailureAction = 'discussion/MARK_MESSAGE_AS_READ_FAILURE';
export const getDraftMessageRequestAction = 'discussion/GET_DRAFT_MESSAGE';
export const getDraftMessageSuccessAction = 'discussion/GET_DRAFT_MESSAGE_SUCCESS';
export const getDraftMessageFailureAction = 'discussion/GET_DRAFT_MESSAGE_FAILURE';
export const saveDraftMessageAction = 'discussion/SAVE_DRAFT_MESSAGE';
export const saveDraftMessageSuccessAction = 'discussion/SAVE_DRAFT_MESSAGE_SUCCESS';
export const saveDraftMessageFailureAction = 'discussion/SAVE_DRAFT_MESSAGE_FAILURE';
export const scheduleMessageRequestAction = 'discussion/SCHEDULE_MESSAGE_REQUEST';
export const scheduleMessageSuccessAction = 'discussion/SCHEDULE_MESSAGE_SUCCESS';
export const scheduleMessageFailureAction = 'discussion/SCHEDULE_MESSAGE_FAILURE';
export const fetchScheduledMessagesRequestAction = 'discussion/FETCH_SCHEDULED_MESSAGES_REQUEST';
export const fetchScheduledMessagesSuccessAction = 'discussion/FETCH_SCHEDULED_MESSAGES_SUCCESS';
export const fetchScheduledMessagesFailureAction = 'discussion/FETCH_SCHEDULED_MESSAGES_FAILURE';
export const deleteScheduledMessageRequestAction = 'discussion/DELETE_SCHEDULED_MESSAGE_REQUEST';
export const deleteScheduledMessageSuccessAction = 'discussion/DELETE_SCHEDULED_MESSAGE_SUCCESS';
export const deleteScheduledMessageFailureAction = 'discussion/DELETE_SCHEDULED_MESSAGE_FAILURE';

// the sendingMessageId is given in case of retry
export const sendMessageRequest = (
    dossierId: DossierId,
    sendingMessageId: string,
    content: string,
    contentHtml?: string,
    attachmentIds?: string[],
): SendMessageRequestAction => ({
    type: sendMessageRequestAction,
    dossierId,
    sendingMessageId,
    content,
    contentHtml,
    attachmentIds,
});

export const sendMessageSuccess = (
    messageId: number,
    dossierId: DossierId,
    sendingMessageId: string,
): SendMessageSuccessAction => ({
    type: sendMessageSuccessAction,
    messageId,
    dossierId,
    sendingMessageId,
});

export const sendMessageFailure = (
    error: string,
    sendingMessageId: string,
    dossierId: DossierId,
): SendMessageFailureAction => ({
    type: sendMessageFailureAction,
    sendingMessageId,
    error,
    dossierId,
});

export const updateMessageRequest = (
    messageId: number,
    dossierId: DossierId,
    content: string,
    contentHtml: string,
    isScheduledMessage: boolean,
): UpdateMessageRequestAction => ({
    type: updateMessageRequestAction,
    messageId,
    dossierId,
    content,
    isScheduledMessage,
});

export const updateMessageFailure = (
    messageId: number,
    dossierId: DossierId,
    error: string,
): UpdateMessageFailureAction => ({
    type: updateMessageFailureAction,
    messageId,
    dossierId,
    error,
});

export const getMessageRequest = (
    messageId: number,
    dossierId: DossierId,
    sendingMessageId?: string,
): GetMessageRequestAction => ({
    type: getMessageRequestAction,
    dossierId,
    messageId,
    sendingMessageId,
});

export const getMessageSuccess = (
    message: Message,
    dossierId: DossierId,
    sendingMessageId?: string,
): GetMessageSuccessAction => ({
    type: getMessageSuccessAction,
    dossierId,
    message,
    sendingMessageId,
});

export const getMessageFailure = (
    error: string,
    dossierId: DossierId,
): GetMessageFailureAction => ({
    type: getMessageFailureAction,
    dossierId,
    error,
});

export const markMessageAsReadRequest = (
    dossierId: DossierId,
    messageId: number,
): MarkMessageAsReadRequestAction => ({
    type: markMessageAsReadRequestAction,
    dossierId,
    messageId,
});

export const markMessageAsReadSuccess = (dossierId: DossierId): MarkMessageAsReadSuccessAction => ({
    type: markMessageAsReadSuccessAction,
    dossierId,
});

export const markMessageAsReadFailure = (error: string): MarkMessageAsReadFailureAction => ({
    type: markMessageAsReadFailureAction,
    error,
});

export const getDraftMessageRequest = (dossierId: DossierId): GetDraftMessageRequestAction => ({
    type: getDraftMessageRequestAction,
    dossierId,
});

export const getDraftMessageSuccess = (
    dossierId: DossierId,
    draftMessage: string,
    htmlDraftMessage: string,
): GetDraftMessageSuccessAction => ({
    type: getDraftMessageSuccessAction,
    dossierId,
    draftMessage,
    htmlDraftMessage,
});

export const getDraftMessageFailure = (
    error: string,
    dossierId: DossierId,
): GetDraftMessageFailureAction => ({
    type: getDraftMessageFailureAction,
    error,
    dossierId,
});

export const saveDraftMessage = (
    dossierId: DossierId,
    draftMessage: string,
    htmlDraftMessage: string,
    metadata?: DraftMessageMetadata,
): SaveDraftMessageAction => ({
    type: saveDraftMessageAction,
    dossierId,
    draftMessage,
    htmlDraftMessage,
    metadata,
});

export const saveDraftMessageSuccess = (dossierId: DossierId): SaveDraftMessageSuccessAction => ({
    type: saveDraftMessageSuccessAction,
    dossierId,
});

export const saveDraftMessageFailure = (
    error: string,
    dossierId: DossierId,
): SaveDraftMessageFailureAction => ({
    type: saveDraftMessageFailureAction,
    error,
    dossierId,
});

export const scheduleMessageRequest = (
    dossierId: DossierId,
    content: string,
    contentHtml: string,
    scheduledDate: Date,
    authorId: string,
    sendingMessageId: string,
    attachmentIds?: string[],
): ScheduleMessageRequestAction => ({
    type: scheduleMessageRequestAction,
    dossierId,
    content,
    contentHtml,
    scheduledDate,
    authorId,
    sendingMessageId,
    attachmentIds,
});

export const scheduleMessageSuccess = (
    id: string,
    dossierId: DossierId,
    content: string,
    contentHtml: string,
    scheduledDate: Date,
    authorId: string,
    sendingMessageId?: string,
): ScheduleMessageSuccessAction => ({
    type: scheduleMessageSuccessAction,
    id,
    dossierId,
    content,
    contentHtml,
    scheduledDate,
    authorId,
    sendingMessageId,
});

export const scheduleMessageFailure = (
    error: string,
    dossierId: DossierId,
): ScheduleMessageFailureAction => ({
    type: scheduleMessageFailureAction,
    error,
    dossierId,
});

export const fetchScheduledMessagesRequest = (
    dossierId: DossierId,
): FetchScheduledMessagesRequestAction => ({
    type: fetchScheduledMessagesRequestAction,
    dossierId,
});

export const fetchScheduledMessagesSuccess = (
    id: string,
    dossierId: DossierId,
    content: string,
    contentHtml: string | undefined,
    scheduledDate: Date,
    authorId: string,
    attachments: MessageAttachment[],
): FetchScheduledMessagesSuccessAction => ({
    type: fetchScheduledMessagesSuccessAction,
    id,
    dossierId,
    content,
    contentHtml,
    scheduledDate,
    authorId,
    attachments,
});

export const fetchScheduledMessagesFailure = (
    error: string,
    dossierId: DossierId,
): FetchScheduledMessagesFailureAction => ({
    type: fetchScheduledMessagesFailureAction,
    error,
    dossierId,
});

export const deleteScheduledMessageRequest = (
    dossierId: DossierId,
    scheduledMessageId: string,
): DeleteScheduledMessageRequestAction => ({
    type: deleteScheduledMessageRequestAction,
    dossierId,
    scheduledMessageId,
});

export const deleteScheduledMessageSuccess = (
    dossierId: DossierId,
): DeleteScheduledMessageSuccessAction => ({
    type: deleteScheduledMessageSuccessAction,
    dossierId,
});

export const deleteScheduledMessageFailure = (
    error: string,
    dossierId: DossierId,
): DeleteScheduledMessageFailureAction => ({
    type: deleteScheduledMessageFailureAction,
    error,
    dossierId,
});
